<template>
  <Teleport to="body">
    <div class="toast-area">
      <ToastComponent :visible="toastVisible" :type="toastType" :message="toastMessage" />
    </div>
  </Teleport>

  <div v-if="visible" class="modal-overlay">
    <div class="modal-container">
      <div class="modal-header">
        <h2 class="modal-title">{{ seasonTitle }}</h2>
        <button class="close-btn" type="button" @click="handleClose">
          <img :src="closeIcon" alt="close" class="close-icon" />
        </button>
      </div>

      <div v-if="loading" class="loading-overlay">
        <LoadingIndicator text="Loading..." />
      </div>

      <div v-else-if="error" class="error-container">
        <p class="error-text">{{ error }}</p>
      </div>

      <div v-else class="modal-body">
        <div class="tab-switcher">
          <button
            v-for="tab in displayItemTypes"
            :key="tab.value"
            class="tab-btn"
            :class="{ active: activeTab === tab.value }"
            type="button"
            @click="activeTab = tab.value"
          >
            {{ tab.label }}
          </button>
        </div>

        <div class="checklist-items">
            <div class="checkbox-wrapper" @click="toggleAll">
              <div class="checkbox" :class="{ checked: isAllSelected }">
                <svg v-if="isAllSelected" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M2 6L5 9L10 3"
                    stroke="white"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <span class="checkbox-label">All</span>
            </div>
            <div
              v-for="item in currentItems"
              :key="item.id"
              class="checkbox-wrapper"
              @click="toggleItem(item.id)"
            >
              <div class="checkbox" :class="{ checked: isSelected(item.id) }">
                <svg v-if="isSelected(item.id)" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M2 6L5 9L10 3"
                    stroke="white"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <span class="item-code">{{ item.item_id }}</span>
              <span class="item-name">{{ item.item_name }}</span>
            </div>
        </div>

        <div class="modal-footer">
          <button class="btn-cancel" type="button" @click="handleClose">Cancel</button>
          <button class="btn-submit" type="button" :disabled="submitting" @click="handleSubmit">
            Submit
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { getChecklistList, type ChecklistItem } from '@/api/checklist'
import {
  getDefaultChecklistDetail,
  updateDefaultChecklistItems,
  type DefaultChecklistMetaOption,
  type DefaultChecklistTemplate,
} from '@/api/defaultChecklist'
import ToastComponent from '@/components/ToastComponent.vue'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import closeIcon from '@/assets/Fill 1@2x.png'

export interface SeasonEditItem {
  id: string
  item_id: string
  item_name: string
}

interface Props {
  visible: boolean
  seasonTitle: string
  templateId?: number | null
  /** 当前季节已选中的 item_id 列表，用于回显勾选 */
  selectedItemCodes: string[]
  itemTypes?: DefaultChecklistMetaOption[]
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved', payload: { items: SeasonEditItem[]; template?: DefaultChecklistTemplate }): void
}>()

const loading = ref(true)
const error = ref('')
const submitting = ref(false)
const activeTab = ref('')

const displayItemTypes = computed<DefaultChecklistMetaOption[]>(() => {
  if (props.itemTypes && props.itemTypes.length > 0) return props.itemTypes
  return [
    { value: 'baseline', label: 'Baseline' },
    { value: 'routine', label: 'Routine' },
  ]
})

const itemsByType = ref<Record<string, ChecklistItem[]>>({})
const selectedItems = ref<Set<number>>(new Set())

const toastVisible = ref(false)
const toastType = ref<'success' | 'warning' | 'error'>('success')
const toastMessage = ref('')
let toastTimer: ReturnType<typeof setTimeout> | null = null

const currentItems = computed<ChecklistItem[]>(() => {
  return itemsByType.value[activeTab.value] ?? []
})

const isAllSelected = computed(() => {
  if (currentItems.value.length === 0) return false
  return currentItems.value.every((item) => selectedItems.value.has(item.id))
})

function showToast(type: 'success' | 'warning' | 'error', message: string): void {
  if (toastTimer) {
    clearTimeout(toastTimer)
  }
  toastType.value = type
  toastMessage.value = message
  toastVisible.value = true
  toastTimer = setTimeout(() => {
    toastVisible.value = false
  }, 3500)
}

function isSelected(itemId: number): boolean {
  return selectedItems.value.has(itemId)
}

function toggleItem(itemId: number): void {
  if (selectedItems.value.has(itemId)) {
    selectedItems.value.delete(itemId)
  } else {
    selectedItems.value.add(itemId)
  }
}

function toggleAll(): void {
  if (isAllSelected.value) {
    currentItems.value.forEach((item) => {
      selectedItems.value.delete(item.id)
    })
  } else {
    currentItems.value.forEach((item) => {
      selectedItems.value.add(item.id)
    })
  }
}

async function loadChecklistItems(): Promise<void> {
  loading.value = true
  error.value = ''

  try {
    const [response, detailRes] = await Promise.all([
      getChecklistList({
        page: 1,
        page_size: 1000,
      }),
      props.templateId != null
        ? getDefaultChecklistDetail({ id: props.templateId, template_id: props.templateId })
        : Promise.resolve(null),
    ])

    if (response.code === 200 && response.data) {
      const list = response.data.list
      const grouped: Record<string, ChecklistItem[]> = {}
      displayItemTypes.value.forEach((tab) => {
        const tabKey = tab.value.trim().toLowerCase()
        grouped[tab.value] = list.filter((item) => (item.type || '').trim().toLowerCase() === tabKey)
      })
      itemsByType.value = grouped

      const codeSet = new Set(props.selectedItemCodes)
      const idSet = new Set<number>()
      if (detailRes && detailRes.code === 200 && detailRes.data) {
        const detail = detailRes.data
        ;(detail.items ?? []).forEach((item) => idSet.add(item.id))
        ;(detail.baseline_items ?? []).forEach((item) => idSet.add(item.id))
        ;(detail.routine_items ?? []).forEach((item) => idSet.add(item.id))
        ;(detail.baseline_checklist_ids ?? []).forEach((id) => idSet.add(id))
        ;(detail.routine_checklist_ids ?? []).forEach((id) => idSet.add(id))
      }

      const nextSelected = new Set<number>()
      Object.values(grouped)
        .flat()
        .forEach((item) => {
          if (idSet.has(item.id) || codeSet.has(item.item_id)) {
            nextSelected.add(item.id)
          }
        })
      selectedItems.value = nextSelected
    } else {
      error.value = response.msg || 'Failed to load checklist items'
    }
  } catch {
    error.value = 'Network error, please try again later'
  } finally {
    loading.value = false
  }
}

async function handleSubmit(): Promise<void> {
  if (submitting.value) return
  if (props.templateId == null) {
    showToast('error', 'Missing template id')
    return
  }

  submitting.value = true
  try {
    const selectedOfType = (typeKey: string): number[] => {
      const key = typeKey.trim().toLowerCase()
      return Object.entries(itemsByType.value)
        .flatMap(([tabValue, list]) =>
          list
            .filter((item) => selectedItems.value.has(item.id))
            .filter((item) => {
              const itemType = (item.type || '').trim().toLowerCase()
              const tabType = tabValue.trim().toLowerCase()
              return itemType === key || tabType === key
            }),
        )
        .map((item) => item.id)
    }

    const baselineIds = [...new Set(selectedOfType('baseline'))]
    const routineIds = [...new Set(selectedOfType('routine'))]

    const response = await updateDefaultChecklistItems({
      template_id: props.templateId,
      baseline_checklist_ids: baselineIds,
      routine_checklist_ids: routineIds,
    })

    if (response.code === 200) {
      const selected = Object.values(itemsByType.value)
        .flat()
        .filter((item) => selectedItems.value.has(item.id))
        .map((item) => ({
          id: String(item.id),
          item_id: item.item_id,
          item_name: item.item_name,
        }))
      emit('saved', { items: selected, template: response.data })
      emit('close')
      showToast('success', 'Successfully saved')
    } else {
      showToast('error', response.msg || 'Failed to save')
    }
  } catch {
    showToast('error', 'Failed to save')
  } finally {
    submitting.value = false
  }
}

function handleClose(): void {
  emit('close')
}

watch(
  () => props.visible,
  (newVal) => {
    if (newVal) {
      loading.value = true
      error.value = ''
      activeTab.value = displayItemTypes.value[0]?.value ?? 'baseline'
      void loadChecklistItems()
    }
  },
)
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.modal-container {
  background-color: #ffffff;
  border-radius: 16px;
  width: 530px;
  height: 90%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
  position: relative;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 16px;
  position: relative;
  z-index: 2;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

:deep(.hm-loading) {
  padding: 0;
}

.modal-title {
  font-family:
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro',
    system-ui,
    sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #000000;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-icon {
  width: 15px;
  height: 15px;
  object-fit: contain;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 80px;
}

.error-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
}

.error-text {
  color: #ff4d4f;
  font-size: 14px;
}

.tab-switcher {
  display: flex;
  background-color: #f5f6fb;
  border-radius: 16px;
  padding: 0;
  margin-bottom: 16px;
  height: 32px;
}

.tab-btn {
  flex: 1;
  height: 32px;
  padding: 0 20px;
  border: none;
  border-radius: 16px;
  background-color: transparent;
  color: #999999;
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn.active {
  background-color: #000000;
  color: #ffffff;
}

.tab-btn:hover:not(.active) {
  color: #999999;
}

.checklist-items {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.checkbox-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  user-select: none;
}

.checkbox {
  width: 20px;
  height: 20px;
  border: 2px solid #e0e0e0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}

.checkbox.checked {
  background-color: #000000;
  border-color: #000000;
}

.checkbox svg {
  width: 12px;
  height: 12px;
}

.checkbox-label {
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
}

.item-code {
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  color: #000000;
  font-weight: 400;
  line-height: 20px;
  flex-shrink: 0;
  padding-right: 10px;
}

.item-name {
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
  flex: 1;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  padding: 16px 24px 24px;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #ffffff;
  border-radius: 0 0 16px 16px;
}

.btn-cancel {
  width: 80px;
  height: 32px;
  padding: 0;
  border: 1px solid #000000;
  border-radius: 24px;
  background-color: #ffffff;
  color: #000000;
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel:hover {
  background-color: #000000;
  color: #ffffff;
}

.btn-submit {
  width: 80px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 24px;
  background-color: #000000;
  color: #ffffff;
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-submit:hover:not(:disabled) {
  background-color: #484848;
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
